import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import {
  computeGranularity,
  toMetricGranularity,
  type DateRangeInput,
  type IngestionFilter,
} from '@/lib/api/system/ingestion-observability';
import {
  FetchMetricQuery,
  MetricCube,
  IngestionMeasure,
  IngestionBreakdown,
} from '@/lib/api/reporting/metrics';

interface IngestionHourlyVolumeProps {
  tenantId: string;
  dateRange: DateRangeInput;
  filters?: IngestionFilter;
}

const STAGE_PALETTE = [
  '#3b82f6', '#06b6d4', '#8b5cf6', '#f59e0b', '#f43f5e',
  '#6366f1', '#10b981', '#ec4899', '#14b8a6', '#f97316',
  '#84cc16', '#a855f7', '#0ea5e9', '#d97706', '#78716c',
  '#e11d48', '#059669', '#7c3aed', '#ca8a04', '#0891b2',
];

export function IngestionHourlyVolume({ tenantId, dateRange, filters }: IngestionHourlyVolumeProps) {
  const granularity = computeGranularity(dateRange);

  const { data, isLoading, error } = useQuery(
    FetchMetricQuery({
      tenantId,
      cube: MetricCube.IngestionEvents,
      measure: IngestionMeasure.EventCount,
      breakdown: IngestionBreakdown.Stage,
      granularity: toMetricGranularity(granularity),
      dateStart: new Date(dateRange.dateStart),
      dateEnd: new Date(dateRange.dateEnd),
      siteId: filters?.projectId,
      cameraId: filters?.videoProcessorConfigId,
    })
  );

  const tickFormat = granularity === 'HOUR'
    ? { month: 'short' as const, day: 'numeric' as const, hour: '2-digit' as const, minute: '2-digit' as const }
    : { month: 'short' as const, day: 'numeric' as const };

  const { chartData, stages, stageColorMap } = useMemo(() => {
    if (!data?.data?.length) return { chartData: [], stages: [], stageColorMap: new Map<string, string>() };

    // Aggregate totals per stage to sort by volume
    const stageTotals: Record<string, number> = {};
    const byBucket: Record<string, Record<string, number>> = {};

    for (const point of data.data) {
      const stage = point.attributes?.['stage'] ?? '';
      const bucket = point.key ?? '';
      const value = point.value ?? 0;

      stageTotals[stage] = (stageTotals[stage] ?? 0) + value;
      if (!byBucket[bucket]) byBucket[bucket] = {};
      byBucket[bucket][stage] = (byBucket[bucket][stage] ?? 0) + value;
    }

    const stages = Object.entries(stageTotals)
      .sort(([, a], [, b]) => b - a)
      .map(([stage]) => stage);

    const stageColorMap = new Map<string, string>();
    stages.forEach((s, i) => stageColorMap.set(s, STAGE_PALETTE[i % STAGE_PALETTE.length]));

    const chartData = Object.entries(byBucket)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([bucket, counts]) => ({
        bucket: new Date(bucket).toLocaleString(undefined, tickFormat),
        ...counts,
      }));

    return { chartData, stages, stageColorMap };
  }, [data, tickFormat]);

  if (isLoading) {
    return (
      <div className="rounded-lg border border-[#2E3835] bg-[#1a1a1a] p-3">
        <Skeleton className="h-[220px] w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-400">
        Failed to load volume data: {error.message}
      </div>
    );
  }

  if (chartData.length === 0) return null;

  return (
    <div className="rounded-lg border border-[#2E3835] bg-[#1a1a1a]">
      <div className="px-3 pt-3 pb-1">
        <h3 className="text-sm font-medium text-white">Volume by Stage</h3>
        <p className="text-[10px] text-white/40">
          {granularity === 'HOUR' ? 'Hourly' : granularity === 'DAY' ? 'Daily' : 'Weekly'} event volume per pipeline stage
        </p>
      </div>

      <div className="px-3 pb-3">
        <div className="h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, bottom: 5, left: 10 }}>
              <defs>
                {stages.map((stage) => {
                  const color = stageColorMap.get(stage)!;
                  return (
                    <linearGradient key={stage} id={`sv-${stage}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.25} />
                      <stop offset="100%" stopColor={color} stopOpacity={0.02} />
                    </linearGradient>
                  );
                })}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2E3835" vertical={false} />
              <XAxis
                dataKey="bucket"
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }}
                interval="preserveStartEnd"
                axisLine={{ stroke: '#2E3835' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={({ active, label, payload }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <div className="rounded-lg border border-[#2E3835] bg-black/95 px-3 py-2 text-xs backdrop-blur-sm">
                      <div className="mb-1.5 font-medium text-white/70">{label}</div>
                      {stages.map((stage) => {
                        const entry = payload.find((p) => p.dataKey === stage);
                        const val = (entry?.value as number) ?? 0;
                        if (val === 0) return null;
                        return (
                          <div key={stage} className="flex items-center gap-1.5 py-0.5">
                            <span
                              className="inline-block h-2 w-2 rounded-sm shrink-0"
                              style={{ backgroundColor: stageColorMap.get(stage) }}
                            />
                            <span className="text-white/70">{stage}</span>
                            <span className="ml-auto pl-4 tabular-nums text-white/50">
                              {val.toLocaleString()}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
                cursor={{ stroke: 'rgba(255,255,255,0.1)' }}
              />
              {stages.map((stage) => (
                <Area
                  key={stage}
                  type="monotone"
                  dataKey={stage}
                  stroke={stageColorMap.get(stage)!}
                  strokeWidth={1.5}
                  fill={`url(#sv-${stage})`}
                  dot={false}
                  stackId="stages"
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
